import { InviteFormUI } from "@/components/pages_logged_in/workspace-admin/InviteFormUI";
import { getUser } from "@/components/session/JwtTokenManager";
import {
  InviteClientsMutation,
  useInviteClientsMutation,
  useUpdateWorkspaceInvitationScheduleMutation,
  useWorkspaceSubscriptionQuery,
  WorkspaceSubscriptionQueryDocument,
} from "@/graphql";
import { WorkspaceInvitationBatch } from "@/types/workspaces";

type InviteClientsResult = NonNullable<InviteClientsMutation["inviteClients"]>;

interface InviteFormProps {
  result?: InviteClientsResult;
  inline?: boolean;
  allowSetCredits?: boolean;
  workspaceCredit?: string | null;
  isEdit?: boolean;
  workspaceInvitationBatch?: WorkspaceInvitationBatch | null;
  onCancel?: () => void;
  onResultChange: (result: InviteClientsResult) => void;
  allowSchedule?: boolean;
  scheduleText?: string;
  allowSetGroup?: boolean;
}

export function InviteForm({
  result,
  inline = false,
  allowSetCredits = false,
  workspaceCredit = "0",
  isEdit = false,
  workspaceInvitationBatch,
  onCancel,
  onResultChange,
  allowSchedule = true,
  scheduleText,
  allowSetGroup = true,
}: InviteFormProps) {
  const [inviteClients, { loading: invitingClients }] =
    useInviteClientsMutation({
      onCompleted: (data) => {
        if (data?.inviteClients) {
          onResultChange(data.inviteClients);
        }
      },
      onError: (error) => {
        onResultChange({
          ok: false,
          errors: [error],
        });
      },
      refetchQueries: [
        {
          query: WorkspaceSubscriptionQueryDocument,
        },
      ],
    });

  const [updateWorkspaceInvitationSchedule, { loading: updatingSchedule }] =
    useUpdateWorkspaceInvitationScheduleMutation({
      onCompleted: (data) => {
        const { ok, toBeInvitedEmails, invalidEmails, existedEmails } =
          data?.updateWorkspaceInvitationSchedule || {};
        onResultChange({
          ok,
          toBeInvitedEmails,
          invalidEmails,
          existedEmails,
        });
      },
      onError: (error) => {
        onResultChange({
          ok: false,
          errors: [error],
        });
      },
      refetchQueries: [
        {
          query: WorkspaceSubscriptionQueryDocument,
        },
      ],
    });

  const { data: workspaceSubscriptionData } = useWorkspaceSubscriptionQuery();
  const planSeats =
    workspaceSubscriptionData?.currentWorkspace?.subscription?.quantity;
  const { numMembers: occupiedSeats, totalPendingInvitations } =
    workspaceSubscriptionData?.currentWorkspace || {};

  const { isSuperuser } = getUser() || {};

  return (
    <InviteFormUI
      result={result}
      inline={inline}
      allowSetCredits={allowSetCredits}
      workspaceCredit={workspaceCredit}
      isEdit={isEdit}
      workspaceInvitationBatch={workspaceInvitationBatch}
      onCancel={onCancel}
      onUserInvite={inviteClients}
      onInvitationScheduleUpdate={updateWorkspaceInvitationSchedule}
      isLoading={invitingClients || updatingSchedule}
      allowSchedule={allowSchedule}
      scheduleButtonText="Schedule Invite"
      scheduleText={scheduleText}
      allowSetGroup={allowSetGroup}
      allowAssignCoaches={isSuperuser}
      planSeats={planSeats}
      occupiedSeats={occupiedSeats}
      totalPendingInvitations={totalPendingInvitations}
    />
  );
}
